import React, { useState, useEffect } from 'react';
import Header from '../components/Layout/Header';
import { taxAPI, employeeAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Calculator, FileText, CheckCircle, PiggyBank, Shield, Home, XCircle, Clock, Send, Eye, AlertCircle, RefreshCw, History, ArrowRight, Edit, Plus, Search, Settings, Save } from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';

const TaxDeclarations = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'hr', 'verifier'].includes(user?.role);
  const currentFY = '2025-26';
  
  const [myDeclarations, setMyDeclarations] = useState([]);
  const [allDeclarations, setAllDeclarations] = useState([]);
  const [taxSettings, setTaxSettings] = useState(null);
  const [taxCalc, setTaxCalc] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedFY, setSelectedFY] = useState(currentFY);
  const [stats, setStats] = useState({ draft: 0, submitted: 0, accepted: 0, rejected: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Admin action states
  const [selectedDeclaration, setSelectedDeclaration] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditTrail, setAuditTrail] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Tax sub-head limits state (admin)
  const [showLimitsModal, setShowLimitsModal] = useState(false);
  const [subheadLimits, setSubheadLimits] = useState({});
  const [limitsDefaults, setLimitsDefaults] = useState({});
  const [limitsDescriptions, setLimitsDescriptions] = useState({});
  const [limitsLoading, setLimitsLoading] = useState(false);
  const [savingLimits, setSavingLimits] = useState(false);
  
  // Employee form limits (for display in create modal)
  const [formLimits, setFormLimits] = useState({});
  
  const [declaration, setDeclaration] = useState({
    financial_year: currentFY,
    tax_regime: 'new',
    section_80c_ppf: 0,
    section_80c_elss: 0,
    section_80c_life_insurance: 0,
    section_80c_nsc: 0,
    section_80c_housing_loan_principal: 0,
    section_80c_children_tuition: 0,
    section_80c_other: 0,
    section_80d_self: 0,
    section_80d_parents: 0,
    section_24_housing_interest: 0,
    section_80e_education_loan: 0,
    hra_rent_paid_annually: 0,
    hra_city_type: 'metro',
    nps_contribution: 0,
    other_deductions: 0
  });

  // Check if selected FY is current FY (only allow entries for current FY)
  const isCurrentFY = selectedFY === currentFY;

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFY]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [decRes, settingsRes] = await Promise.all([
        taxAPI.getMyDeclarations(selectedFY),
        taxAPI.getSettings()
      ]);
      setMyDeclarations(decRes.data);
      setTaxSettings(settingsRes.data);
      
      if (decRes.data.length > 0) {
        setDeclaration(prev => ({...prev, ...decRes.data[0]}));
      }

      if (isAdmin) {
        const [allDecRes, empRes, statsRes] = await Promise.all([
          taxAPI.getAllDeclarations(selectedFY),
          employeeAPI.getAll(),
          taxAPI.getStats(selectedFY)
        ]);
        setAllDeclarations(allDecRes.data);
        setEmployees(empRes.data);
        setStats(statsRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubheadLimits = async () => {
    setLimitsLoading(true);
    try {
      const [limitsRes, defaultsRes] = await Promise.all([
        taxAPI.getSubheadLimits(),
        taxAPI.getDefaultSubheadLimits()
      ]);
      setSubheadLimits(limitsRes.data.limits || {});
      setLimitsDefaults(defaultsRes.data.defaults || {});
      setLimitsDescriptions(defaultsRes.data.descriptions || {});
    } catch (error) {
      toast.error('Failed to fetch tax limits');
    } finally {
      setLimitsLoading(false);
    }
  };

  const openLimitsModal = async () => {
    setShowLimitsModal(true);
    await fetchSubheadLimits();
  };

  const handleSaveLimits = async () => {
    setSavingLimits(true);
    try {
      await taxAPI.updateSubheadLimits(subheadLimits);
      toast.success('Tax sub-head limits updated successfully');
      setShowLimitsModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update limits');
    } finally {
      setSavingLimits(false);
    }
  };

  const handleLimitChange = (key, value) => {
    const numValue = parseFloat(value) || 0;
    setSubheadLimits(prev => ({...prev, [key]: numValue}));
  };

  const resetToDefaults = () => {
    setSubheadLimits({...limitsDefaults});
    toast.info('Limits reset to defaults (unsaved)');
  };

  // Fetch limits for employee declaration form
  const fetchFormLimits = async () => {
    try {
      const res = await taxAPI.getSubheadLimits();
      setFormLimits(res.data.limits || {});
    } catch (error) {
      // Use defaults on error
      setFormLimits({
        section_80c_total: 150000,
        section_80d_self: 25000,
        section_80d_parents: 50000,
        section_24_housing_interest: 200000,
        nps_contribution: 50000
      });
    }
  };

  const openCreateModal = async () => {
    setShowCreate(true);
    if (!isAdmin) {
      await fetchFormLimits();
    }
  };

  // Helper to format limit display
  const formatLimit = (key) => {
    const limit = formLimits[key];
    if (!limit || limit === 0) return null;
    return `Max: ₹${limit.toLocaleString()}`;
  };

  const calculateTax = async () => {
    try {
      const employee = employees.find(e => e.email === user.email);
      if (!employee && !isAdmin) {
        toast.error('Employee profile not found');
        return;
      }
      const res = await taxAPI.calculateTax(employee?.id || employees[0]?.id, selectedFY);
      setTaxCalc(res.data);
    } catch (error) {
      toast.error('Failed to calculate tax');
    }
  };

  const handleSaveDeclaration = async () => {
    try {
      const response = await taxAPI.createDeclaration(declaration);
      const violations = response.data?.limit_violations || [];
      
      if (violations.length > 0) {
        // Show warning with capped fields
        const violationList = violations.map(v => `${v.field_name}: ₹${v.declared.toLocaleString()} → ₹${v.limit.toLocaleString()}`).join('\n');
        toast.warning(
          `Declaration saved with adjusted limits:\n${violationList}`,
          { duration: 6000 }
        );
      } else {
        toast.success('Declaration saved successfully');
      }
      setShowCreate(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save declaration');
    }
  };

  const handleSubmitDeclaration = async () => {
    if (myDeclarations.length === 0) {
      toast.error('Please save your declaration first');
      return;
    }
    try {
      await taxAPI.submitDeclaration(myDeclarations[0].id);
      toast.success('Declaration submitted for approval');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit declaration');
    }
  };

  const handleAccept = async (declarationId) => {
    setProcessing(true);
    try {
      await taxAPI.acceptDeclaration(declarationId);
      toast.success('Declaration accepted');
      setShowViewDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setProcessing(true);
    try {
      await taxAPI.rejectDeclaration(selectedDeclaration.id, rejectionReason);
      toast.success('Declaration rejected');
      setShowRejectDialog(false);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const openViewDialog = (dec) => {
    setSelectedDeclaration(dec);
    setShowViewDialog(true);
  };

  const openRejectDialog = (dec) => {
    setSelectedDeclaration(dec);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const openAuditDialog = async (dec) => {
    setSelectedDeclaration(dec);
    setAuditTrail([]);
    setShowAuditDialog(true);
    setAuditLoading(true);
    try {
      const res = await taxAPI.getAuditTrail(dec.id);
      setAuditTrail(res.data);
    } catch (error) {
      toast.error('Failed to load audit trail');
    } finally {
      setAuditLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'created': return <Plus className="w-4 h-4 text-blue-500" />;
      case 'updated': return <Edit className="w-4 h-4 text-slate-500" />;
      case 'submitted': return <Send className="w-4 h-4 text-amber-500" />;
      case 'resubmitted': return <RefreshCw className="w-4 h-4 text-amber-500" />;
      case 'accepted': return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      'created': 'Declaration Created',
      'updated': 'Declaration Updated',
      'submitted': 'Submitted for Approval',
      'resubmitted': 'Resubmitted for Approval',
      'accepted': 'Declaration Accepted',
      'rejected': 'Declaration Rejected'
    };
    return labels[action] || action;
  };

  const calculate80CTotal = () => {
    const total = (
      parseFloat(declaration.section_80c_ppf || 0) +
      parseFloat(declaration.section_80c_elss || 0) +
      parseFloat(declaration.section_80c_life_insurance || 0) +
      parseFloat(declaration.section_80c_nsc || 0) +
      parseFloat(declaration.section_80c_housing_loan_principal || 0) +
      parseFloat(declaration.section_80c_children_tuition || 0) +
      parseFloat(declaration.section_80c_other || 0)
    );
    return Math.min(total, 150000);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      submitted: 'bg-amber-100 text-amber-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      verified: 'bg-blue-100 text-blue-700'
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {status?.toUpperCase()}
      </span>
    );
  };

  const filteredDeclarations = allDeclarations.filter(d => {
    const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
    const matchesSearch = !searchTerm || 
      d.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.employee_code?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div data-testid="tax-declarations-page">
      <Header 
        title="Tax & Declarations" 
        subtitle={isAdmin ? "Review and approve employee tax declarations" : "Manage investment declarations and tax calculations"}
        action={
          isAdmin ? (
            <Button onClick={openLimitsModal} variant="outline" data-testid="configure-limits-btn">
              <Settings className="w-4 h-4 mr-2" />
              Configure Limits
            </Button>
          ) : (
            isCurrentFY && (
              <Button onClick={openCreateModal} data-testid="add-declaration-btn">
                <FileText className="w-4 h-4 mr-2" />
                {myDeclarations.length > 0 ? 'Update Declaration' : 'Add Declaration'}
              </Button>
            )
          )
        }
      />
      
      <div className="p-8">
        {/* FY Selector */}
        <div className="flex items-center gap-4 mb-8">
          <Label>Financial Year:</Label>
          <Select value={selectedFY} onValueChange={setSelectedFY}>
            <SelectTrigger className="w-40" data-testid="fy-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2027-28">2027-28</SelectItem>
              <SelectItem value="2026-27">2026-27</SelectItem>
              <SelectItem value="2025-26">2025-26 (Current)</SelectItem>
              <SelectItem value="2024-25">2024-25</SelectItem>
              <SelectItem value="2023-24">2023-24</SelectItem>
            </SelectContent>
          </Select>
          {!isCurrentFY && !isAdmin && (
            <span className="text-sm text-amber-600 bg-amber-50 px-2 py-1 rounded">
              View only - entries allowed only for current FY ({currentFY})
            </span>
          )}
        </div>

        <Tabs defaultValue={isAdmin ? "all-declarations" : "my-declaration"} className="space-y-6">
          <TabsList>
            {!isAdmin && <TabsTrigger value="my-declaration">My Declaration</TabsTrigger>}
            {!isAdmin && <TabsTrigger value="tax-calculator">Tax Calculator</TabsTrigger>}
            {isAdmin && (
              <TabsTrigger value="all-declarations" data-testid="all-declarations-tab">
                Employee Declarations
                {stats.submitted > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded-full">
                    {stats.submitted}
                  </span>
                )}
              </TabsTrigger>
            )}
          </TabsList>

          {/* My Declaration Tab - Only for Employees */}
          {!isAdmin && (
          <TabsContent value="my-declaration">
            {myDeclarations.length > 0 ? (
              <div className="dashboard-card" data-testid="my-declaration-card">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-lg font-semibold">My Investment Declaration - FY {selectedFY}</h2>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(myDeclarations[0].status)}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openAuditDialog(myDeclarations[0])}
                      data-testid="my-declaration-history-btn"
                    >
                      <History className="w-4 h-4 mr-1" />
                      History
                    </Button>
                    {myDeclarations[0].status === 'draft' && (
                      <Button onClick={handleSubmitDeclaration} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-declaration-btn">
                        <Send className="w-4 h-4 mr-2" />
                        Submit for Approval
                      </Button>
                    )}
                    {myDeclarations[0].status === 'rejected' && (
                      <Button onClick={handleSubmitDeclaration} className="bg-amber-600 hover:bg-amber-700" data-testid="resubmit-declaration-btn">
                        <Send className="w-4 h-4 mr-2" />
                        Resubmit
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Rejection Alert */}
                {myDeclarations[0].status === 'rejected' && myDeclarations[0].rejection_reason && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800">Declaration Rejected</p>
                        <p className="text-sm text-red-600 mt-1">{myDeclarations[0].rejection_reason}</p>
                        <p className="text-xs text-red-500 mt-2">
                          Rejected by {myDeclarations[0].rejected_by_name} on {formatDate(myDeclarations[0].rejected_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Accepted Alert */}
                {myDeclarations[0].status === 'accepted' && (
                  <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-emerald-800">Declaration Accepted</p>
                        <p className="text-xs text-emerald-600 mt-1">
                          Accepted by {myDeclarations[0].accepted_by_name} on {formatDate(myDeclarations[0].accepted_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Section 80C */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <PiggyBank className="w-5 h-5 text-blue-600" />
                      <h3 className="font-medium text-blue-900">Section 80C (Max ₹1.5L)</h3>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>PPF</span>
                        <span className="font-medium">{formatCurrency(myDeclarations[0].section_80c_ppf)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>ELSS</span>
                        <span className="font-medium">{formatCurrency(myDeclarations[0].section_80c_elss)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Life Insurance</span>
                        <span className="font-medium">{formatCurrency(myDeclarations[0].section_80c_life_insurance)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>NSC</span>
                        <span className="font-medium">{formatCurrency(myDeclarations[0].section_80c_nsc)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Housing Loan Principal</span>
                        <span className="font-medium">{formatCurrency(myDeclarations[0].section_80c_housing_loan_principal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Children Tuition</span>
                        <span className="font-medium">{formatCurrency(myDeclarations[0].section_80c_children_tuition)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Section 80D */}
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-5 h-5 text-green-600" />
                      <h3 className="font-medium text-green-900">Section 80D (Health)</h3>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Self & Family (Max ₹25K)</span>
                        <span className="font-medium">{formatCurrency(myDeclarations[0].section_80d_self)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Parents (Max ₹50K)</span>
                        <span className="font-medium">{formatCurrency(myDeclarations[0].section_80d_parents)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Other Sections */}
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Home className="w-5 h-5 text-purple-600" />
                      <h3 className="font-medium text-purple-900">Other Deductions</h3>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>HRA (Section 10)</span>
                        <span className="font-medium">{formatCurrency(myDeclarations[0].hra_rent_paid_annually)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Housing Loan Interest (24)</span>
                        <span className="font-medium">{formatCurrency(myDeclarations[0].section_24_housing_interest)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Education Loan (80E)</span>
                        <span className="font-medium">{formatCurrency(myDeclarations[0].section_80e_education_loan)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>NPS (80CCD)</span>
                        <span className="font-medium">{formatCurrency(myDeclarations[0].nps_contribution)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-slate-100 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-slate-500">Total Declared Deductions</p>
                      <p className="text-2xl font-bold text-slate-900">{formatCurrency(myDeclarations[0].total_declared)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Tax Regime</p>
                      <p className="text-lg font-semibold text-slate-700 uppercase">{myDeclarations[0].tax_regime}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="dashboard-card text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">No Declaration Found</h3>
                <p className="text-slate-500 mb-4">Submit your investment declaration for FY {selectedFY}</p>
                <Button onClick={openCreateModal}>Add Declaration</Button>
              </div>
            )}
          </TabsContent>
          )}

          {/* Tax Calculator Tab - Only for Employees */}
          {!isAdmin && (
          <TabsContent value="tax-calculator">
            <div className="dashboard-card" data-testid="tax-calculator">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-lg font-semibold">Tax Calculator</h2>
                <Button onClick={calculateTax} data-testid="calculate-tax-btn">
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate Tax
                </Button>
              </div>
              
              {taxCalc ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Gross Annual Income</p>
                      <p className="text-xl font-bold">{formatCurrency(taxCalc.gross_income)}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Standard Deduction</p>
                      <p className="text-xl font-bold text-red-500">- {formatCurrency(taxCalc.standard_deduction)}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Other Deductions</p>
                      <p className="text-xl font-bold text-red-500">- {formatCurrency(taxCalc.other_deductions)}</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <p className="text-sm text-amber-700">Taxable Income</p>
                      <p className="text-xl font-bold text-amber-900">{formatCurrency(taxCalc.taxable_income)}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Tax (Before Cess)</p>
                      <p className="text-xl font-bold">{formatCurrency(taxCalc.tax_before_cess)}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Health & Education Cess (4%)</p>
                      <p className="text-xl font-bold">{formatCurrency(taxCalc.cess)}</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg">
                      <p className="text-sm text-red-700">Total Annual Tax</p>
                      <p className="text-2xl font-bold text-red-900">{formatCurrency(taxCalc.total_tax)}</p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg">
                      <p className="text-sm text-emerald-700">Monthly TDS</p>
                      <p className="text-2xl font-bold text-emerald-900">{formatCurrency(taxCalc.monthly_tds)}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <Calculator className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Click &quot;Calculate Tax&quot; to see your tax liability</p>
                </div>
              )}
            </div>
          </TabsContent>
          )}

          {/* All Declarations Tab (Admin) */}
          {isAdmin && (
            <TabsContent value="all-declarations">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <div className="dashboard-card cursor-pointer hover:ring-2 hover:ring-amber-300" onClick={() => setStatusFilter('submitted')} data-testid="pending-stat">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase">Pending</p>
                      <p className="text-2xl font-bold text-amber-600">{stats.submitted}</p>
                    </div>
                    <Clock className="w-8 h-8 text-amber-200" />
                  </div>
                </div>
                <div className="dashboard-card cursor-pointer hover:ring-2 hover:ring-emerald-300" onClick={() => setStatusFilter('accepted')} data-testid="accepted-stat">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase">Accepted</p>
                      <p className="text-2xl font-bold text-emerald-600">{stats.accepted}</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-emerald-200" />
                  </div>
                </div>
                <div className="dashboard-card cursor-pointer hover:ring-2 hover:ring-red-300" onClick={() => setStatusFilter('rejected')} data-testid="rejected-stat">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase">Rejected</p>
                      <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                    </div>
                    <XCircle className="w-8 h-8 text-red-200" />
                  </div>
                </div>
                <div className="dashboard-card cursor-pointer hover:ring-2 hover:ring-gray-300" onClick={() => setStatusFilter('draft')} data-testid="draft-stat">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase">Draft</p>
                      <p className="text-2xl font-bold text-gray-600">{stats.draft}</p>
                    </div>
                    <FileText className="w-8 h-8 text-gray-200" />
                  </div>
                </div>
                <div className="dashboard-card cursor-pointer hover:ring-2 hover:ring-slate-300" onClick={() => setStatusFilter('all')} data-testid="total-stat">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase">Total</p>
                      <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                    </div>
                    <RefreshCw className="w-8 h-8 text-slate-200" />
                  </div>
                </div>
              </div>

              <div className="dashboard-card" data-testid="all-declarations">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg font-semibold">
                    {statusFilter === 'all' ? 'All' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Declarations - FY {selectedFY}
                  </h2>
                  <div className="flex items-center gap-3">
                    {/* Search input */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search employee..."
                        className="pl-9 w-48"
                        data-testid="search-employee"
                      />
                    </div>
                    {statusFilter !== 'all' && (
                      <Button variant="outline" size="sm" onClick={() => setStatusFilter('all')}>
                        Clear Filter
                      </Button>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Employee</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Regime</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Total Declared</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Submitted</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDeclarations.map((dec) => (
                        <tr key={dec.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`declaration-row-${dec.id}`}>
                          <td className="py-3 px-4">
                            <p className="font-medium">{dec.employee_name}</p>
                            <p className="text-xs text-slate-400">{dec.employee_code}</p>
                          </td>
                          <td className="py-3 px-4 uppercase font-medium">{dec.tax_regime}</td>
                          <td className="py-3 px-4 font-semibold">{formatCurrency(dec.total_declared)}</td>
                          <td className="py-3 px-4">{getStatusBadge(dec.status)}</td>
                          <td className="py-3 px-4 text-sm text-slate-500">
                            {dec.submitted_at ? formatDate(dec.submitted_at) : '-'}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => openViewDialog(dec)}
                                data-testid={`view-declaration-${dec.id}`}
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openAuditDialog(dec)}
                                data-testid={`audit-declaration-${dec.id}`}
                                title="View History"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                              {(dec.status === 'submitted' || dec.status === 'draft') && (
                                <>
                                  <Button 
                                    size="sm" 
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    onClick={() => handleAccept(dec.id)}
                                    data-testid={`accept-declaration-${dec.id}`}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Accept
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                    onClick={() => openRejectDialog(dec)}
                                    data-testid={`reject-declaration-${dec.id}`}
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Reject
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredDeclarations.length === 0 && (
                    <p className="text-center py-8 text-slate-400">No declarations found</p>
                  )}
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Create/Update Declaration Modal - Only for Employees */}
        {!isAdmin && (
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Investment Declaration - FY {declaration.financial_year}</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Tax Regime */}
              <div>
                <Label>Tax Regime</Label>
                <Select value={declaration.tax_regime} onValueChange={(v) => setDeclaration({...declaration, tax_regime: v})}>
                  <SelectTrigger data-testid="regime-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New Regime (Lower rates, fewer deductions)</SelectItem>
                    <SelectItem value="old">Old Regime (Higher rates, more deductions)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Section 80C */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium">Section 80C Investments {formLimits.section_80c_total ? `(Max ₹${formLimits.section_80c_total.toLocaleString()})` : '(Max ₹1,50,000)'}</h3>
                  <span className="text-sm font-medium text-blue-600">Total: {formatCurrency(calculate80CTotal())}</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">PPF {formatLimit('section_80c_ppf') && <span className="text-slate-400 ml-1">({formatLimit('section_80c_ppf')})</span>}</Label>
                    <Input type="number" value={declaration.section_80c_ppf} onChange={(e) => setDeclaration({...declaration, section_80c_ppf: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label className="text-xs">ELSS Mutual Funds {formatLimit('section_80c_elss') && <span className="text-slate-400 ml-1">({formatLimit('section_80c_elss')})</span>}</Label>
                    <Input type="number" value={declaration.section_80c_elss} onChange={(e) => setDeclaration({...declaration, section_80c_elss: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label className="text-xs">Life Insurance Premium {formatLimit('section_80c_life_insurance') && <span className="text-slate-400 ml-1">({formatLimit('section_80c_life_insurance')})</span>}</Label>
                    <Input type="number" value={declaration.section_80c_life_insurance} onChange={(e) => setDeclaration({...declaration, section_80c_life_insurance: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label className="text-xs">NSC {formatLimit('section_80c_nsc') && <span className="text-slate-400 ml-1">({formatLimit('section_80c_nsc')})</span>}</Label>
                    <Input type="number" value={declaration.section_80c_nsc} onChange={(e) => setDeclaration({...declaration, section_80c_nsc: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label className="text-xs">Housing Loan Principal {formatLimit('section_80c_housing_loan_principal') && <span className="text-slate-400 ml-1">({formatLimit('section_80c_housing_loan_principal')})</span>}</Label>
                    <Input type="number" value={declaration.section_80c_housing_loan_principal} onChange={(e) => setDeclaration({...declaration, section_80c_housing_loan_principal: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label className="text-xs">Children Tuition Fees {formatLimit('section_80c_children_tuition') && <span className="text-slate-400 ml-1">({formatLimit('section_80c_children_tuition')})</span>}</Label>
                    <Input type="number" value={declaration.section_80c_children_tuition} onChange={(e) => setDeclaration({...declaration, section_80c_children_tuition: parseFloat(e.target.value) || 0})} />
                  </div>
                </div>
              </div>
              
              {/* Section 80D */}
              <div className="p-4 border rounded-lg">
                <h3 className="font-medium mb-4">Section 80D - Health Insurance</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Self & Family {formLimits.section_80d_self ? `(Max ₹${formLimits.section_80d_self.toLocaleString()})` : '(Max ₹25,000)'}</Label>
                    <Input type="number" value={declaration.section_80d_self} onChange={(e) => setDeclaration({...declaration, section_80d_self: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label className="text-xs">Parents {formLimits.section_80d_parents ? `(Max ₹${formLimits.section_80d_parents.toLocaleString()})` : '(Max ₹50,000)'}</Label>
                    <Input type="number" value={declaration.section_80d_parents} onChange={(e) => setDeclaration({...declaration, section_80d_parents: parseFloat(e.target.value) || 0})} />
                  </div>
                </div>
              </div>
              
              {/* Other Deductions */}
              <div className="p-4 border rounded-lg">
                <h3 className="font-medium mb-4">Other Deductions</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Housing Loan Interest (Sec 24) {formLimits.section_24_housing_interest ? `(Max ₹${formLimits.section_24_housing_interest.toLocaleString()})` : '(Max ₹2L)'}</Label>
                    <Input type="number" value={declaration.section_24_housing_interest} onChange={(e) => setDeclaration({...declaration, section_24_housing_interest: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label className="text-xs">Education Loan Interest (Sec 80E) {formLimits.section_80e_education_loan === 0 ? '(No limit)' : formatLimit('section_80e_education_loan') && `(${formatLimit('section_80e_education_loan')})`}</Label>
                    <Input type="number" value={declaration.section_80e_education_loan} onChange={(e) => setDeclaration({...declaration, section_80e_education_loan: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label className="text-xs">NPS Contribution (80CCD) {formLimits.nps_contribution ? `(Max ₹${formLimits.nps_contribution.toLocaleString()})` : '(Max ₹50K)'}</Label>
                    <Input type="number" value={declaration.nps_contribution} onChange={(e) => setDeclaration({...declaration, nps_contribution: parseFloat(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label className="text-xs">Annual Rent Paid (for HRA exemption)</Label>
                    <Input type="number" value={declaration.hra_rent_paid_annually} onChange={(e) => setDeclaration({...declaration, hra_rent_paid_annually: parseFloat(e.target.value) || 0})} />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleSaveDeclaration} data-testid="save-declaration-btn">Save Declaration</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}

        {/* View Declaration Modal */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Declaration Details</DialogTitle>
            </DialogHeader>
            {selectedDeclaration && (
              <div className="space-y-6 py-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-lg">{selectedDeclaration.employee_name}</p>
                    <p className="text-sm text-slate-500">{selectedDeclaration.employee_code}</p>
                  </div>
                  {getStatusBadge(selectedDeclaration.status)}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600">Tax Regime</p>
                    <p className="font-medium uppercase">{selectedDeclaration.tax_regime}</p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-emerald-600">Total Declared</p>
                    <p className="font-bold text-lg">{formatCurrency(selectedDeclaration.total_declared)}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-slate-500">SECTION 80C</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between"><span>PPF</span><span>{formatCurrency(selectedDeclaration.section_80c_ppf)}</span></div>
                    <div className="flex justify-between"><span>ELSS</span><span>{formatCurrency(selectedDeclaration.section_80c_elss)}</span></div>
                    <div className="flex justify-between"><span>Life Insurance</span><span>{formatCurrency(selectedDeclaration.section_80c_life_insurance)}</span></div>
                    <div className="flex justify-between"><span>NSC</span><span>{formatCurrency(selectedDeclaration.section_80c_nsc)}</span></div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-slate-500">SECTION 80D (HEALTH)</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between"><span>Self & Family</span><span>{formatCurrency(selectedDeclaration.section_80d_self)}</span></div>
                    <div className="flex justify-between"><span>Parents</span><span>{formatCurrency(selectedDeclaration.section_80d_parents)}</span></div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-slate-500">OTHER DEDUCTIONS</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between"><span>HRA</span><span>{formatCurrency(selectedDeclaration.hra_rent_paid_annually)}</span></div>
                    <div className="flex justify-between"><span>Housing Interest</span><span>{formatCurrency(selectedDeclaration.section_24_housing_interest)}</span></div>
                    <div className="flex justify-between"><span>Education Loan</span><span>{formatCurrency(selectedDeclaration.section_80e_education_loan)}</span></div>
                    <div className="flex justify-between"><span>NPS</span><span>{formatCurrency(selectedDeclaration.nps_contribution)}</span></div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
              {selectedDeclaration?.status === 'submitted' && (
                <>
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleAccept(selectedDeclaration.id)}
                    disabled={processing}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Accept
                  </Button>
                  <Button 
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => {
                      setShowViewDialog(false);
                      openRejectDialog(selectedDeclaration);
                    }}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Reject
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Declaration Modal */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Reject Declaration</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {selectedDeclaration && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{selectedDeclaration.employee_name}</p>
                  <p className="text-sm text-slate-500">Total: {formatCurrency(selectedDeclaration.total_declared)}</p>
                </div>
              )}
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">
                    The employee will be notified about the rejection and can resubmit after making corrections.
                  </p>
                </div>
              </div>
              <div>
                <Label>Rejection Reason *</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejection..."
                  rows={3}
                  data-testid="rejection-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)} disabled={processing}>Cancel</Button>
              <Button 
                className="bg-red-600 hover:bg-red-700"
                onClick={handleReject}
                disabled={processing}
                data-testid="confirm-reject-btn"
              >
                {processing ? 'Processing...' : 'Reject Declaration'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Audit Trail Dialog */}
        <Dialog open={showAuditDialog} onOpenChange={setShowAuditDialog}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Declaration History
              </DialogTitle>
            </DialogHeader>
            
            {selectedDeclaration && (
              <div className="py-2">
                <div className="p-3 bg-slate-50 rounded-lg mb-4">
                  <p className="font-medium">{selectedDeclaration.employee_name}</p>
                  <p className="text-sm text-slate-500">FY {selectedDeclaration.financial_year} • {formatCurrency(selectedDeclaration.total_declared)}</p>
                </div>
                
                {auditLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="spinner w-6 h-6"></div>
                  </div>
                ) : auditTrail.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No history available</p>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-[18px] top-2 bottom-2 w-0.5 bg-slate-200"></div>
                    
                    <div className="space-y-4">
                      {auditTrail.map((entry, index) => {
                        // Status color mapping - avoid nested ternaries
                        const getStatusColor = (status) => {
                          const colorMap = {
                            accepted: 'bg-emerald-100 text-emerald-700',
                            rejected: 'bg-red-100 text-red-700',
                            submitted: 'bg-amber-100 text-amber-700'
                          };
                          return colorMap[status] || 'bg-slate-100 text-slate-700';
                        };
                        
                        return (
                          <div key={entry.id} className="relative flex gap-4" data-testid={`audit-entry-${index}`}>
                            {/* Timeline dot */}
                            <div className="relative z-10 flex-shrink-0 w-9 h-9 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center">
                              {getActionIcon(entry.action)}
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 pb-4">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-slate-900">{getActionLabel(entry.action)}</span>
                                {entry.from_status && entry.to_status && (
                                  <span className="flex items-center gap-1 text-xs text-slate-500">
                                    <span className="px-1.5 py-0.5 rounded bg-slate-100 uppercase">{entry.from_status}</span>
                                    <ArrowRight className="w-3 h-3" />
                                    <span className={`px-1.5 py-0.5 rounded uppercase ${getStatusColor(entry.to_status)}`}>{entry.to_status}</span>
                                  </span>
                                )}
                              </div>
                              
                              <p className="text-sm text-slate-500 mt-1">
                                by <span className="font-medium">{entry.user_name}</span>
                              </p>
                              
                              <p className="text-xs text-slate-400 mt-0.5">
                              {formatDate(entry.timestamp)}
                            </p>
                            
                            {/* Show details */}
                            {entry.details && Object.keys(entry.details).length > 0 && (
                              <div className="mt-2 p-2 bg-slate-50 rounded text-xs text-slate-600">
                                {entry.details.reason && (
                                  <div className="flex gap-1">
                                    <span className="text-red-600 font-medium">Reason:</span>
                                    <span>{entry.details.reason}</span>
                                  </div>
                                )}
                                {entry.details.total_declared && (
                                  <div className="flex gap-1">
                                    <span className="font-medium">Amount:</span>
                                    <span>{formatCurrency(entry.details.total_declared)}</span>
                                  </div>
                                )}
                                {entry.details.tax_regime && (
                                  <div className="flex gap-1">
                                    <span className="font-medium">Regime:</span>
                                    <span className="uppercase">{entry.details.tax_regime}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAuditDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tax Sub-head Limits Configuration Modal (Admin Only) */}
        {isAdmin && (
          <Dialog open={showLimitsModal} onOpenChange={setShowLimitsModal}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Tax Sub-head Limits Configuration
                </DialogTitle>
              </DialogHeader>
              
              {limitsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="spinner w-8 h-8"></div>
                </div>
              ) : (
                <div className="space-y-6 py-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-700">
                        <p className="font-medium">Configure maximum limits for tax declarations</p>
                        <p className="mt-1">These limits will be enforced when employees submit their investment declarations. Set to 0 for unlimited.</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Section 80C Limits */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-4">
                      <PiggyBank className="w-5 h-5 text-blue-600" />
                      <h3 className="font-medium text-blue-900">Section 80C Limits</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">80C Overall Limit</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.section_80c_total || ''} 
                          onChange={(e) => handleLimitChange('section_80c_total', e.target.value)}
                          placeholder={limitsDefaults.section_80c_total?.toString()}
                          data-testid="limit-80c-total"
                        />
                        <p className="text-xs text-slate-400 mt-1">{limitsDescriptions.section_80c_total}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">PPF</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.section_80c_ppf || ''} 
                          onChange={(e) => handleLimitChange('section_80c_ppf', e.target.value)}
                          placeholder={limitsDefaults.section_80c_ppf?.toString()}
                          data-testid="limit-80c-ppf"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">ELSS Mutual Funds</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.section_80c_elss || ''} 
                          onChange={(e) => handleLimitChange('section_80c_elss', e.target.value)}
                          placeholder={limitsDefaults.section_80c_elss?.toString()}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Life Insurance</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.section_80c_life_insurance || ''} 
                          onChange={(e) => handleLimitChange('section_80c_life_insurance', e.target.value)}
                          placeholder={limitsDefaults.section_80c_life_insurance?.toString()}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">NSC</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.section_80c_nsc || ''} 
                          onChange={(e) => handleLimitChange('section_80c_nsc', e.target.value)}
                          placeholder={limitsDefaults.section_80c_nsc?.toString()}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Housing Loan Principal</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.section_80c_housing_loan_principal || ''} 
                          onChange={(e) => handleLimitChange('section_80c_housing_loan_principal', e.target.value)}
                          placeholder={limitsDefaults.section_80c_housing_loan_principal?.toString()}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Children Tuition</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.section_80c_children_tuition || ''} 
                          onChange={(e) => handleLimitChange('section_80c_children_tuition', e.target.value)}
                          placeholder={limitsDefaults.section_80c_children_tuition?.toString()}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Other 80C Investments</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.section_80c_other || ''} 
                          onChange={(e) => handleLimitChange('section_80c_other', e.target.value)}
                          placeholder={limitsDefaults.section_80c_other?.toString()}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 80D Limits */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-4">
                      <Shield className="w-5 h-5 text-green-600" />
                      <h3 className="font-medium text-green-900">Section 80D - Health Insurance</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">Self & Family</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.section_80d_self || ''} 
                          onChange={(e) => handleLimitChange('section_80d_self', e.target.value)}
                          placeholder={limitsDefaults.section_80d_self?.toString()}
                          data-testid="limit-80d-self"
                        />
                        <p className="text-xs text-slate-400 mt-1">{limitsDescriptions.section_80d_self}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Parents (Below 60)</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.section_80d_parents || ''} 
                          onChange={(e) => handleLimitChange('section_80d_parents', e.target.value)}
                          placeholder={limitsDefaults.section_80d_parents?.toString()}
                        />
                        <p className="text-xs text-slate-400 mt-1">{limitsDescriptions.section_80d_parents}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Parents (60+)</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.section_80d_parents_senior || ''} 
                          onChange={(e) => handleLimitChange('section_80d_parents_senior', e.target.value)}
                          placeholder={limitsDefaults.section_80d_parents_senior?.toString()}
                        />
                        <p className="text-xs text-slate-400 mt-1">{limitsDescriptions.section_80d_parents_senior}</p>
                      </div>
                    </div>
                  </div>

                  {/* Other Deductions */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-4">
                      <Home className="w-5 h-5 text-purple-600" />
                      <h3 className="font-medium text-purple-900">Other Deductions</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">Housing Loan Interest (Section 24)</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.section_24_housing_interest || ''} 
                          onChange={(e) => handleLimitChange('section_24_housing_interest', e.target.value)}
                          placeholder={limitsDefaults.section_24_housing_interest?.toString()}
                          data-testid="limit-sec24"
                        />
                        <p className="text-xs text-slate-400 mt-1">{limitsDescriptions.section_24_housing_interest}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Education Loan Interest (80E)</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.section_80e_education_loan || ''} 
                          onChange={(e) => handleLimitChange('section_80e_education_loan', e.target.value)}
                          placeholder={limitsDefaults.section_80e_education_loan?.toString()}
                        />
                        <p className="text-xs text-slate-400 mt-1">{limitsDescriptions.section_80e_education_loan} (0 = no limit)</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">NPS Contribution (80CCD)</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.nps_contribution || ''} 
                          onChange={(e) => handleLimitChange('nps_contribution', e.target.value)}
                          placeholder={limitsDefaults.nps_contribution?.toString()}
                          data-testid="limit-nps"
                        />
                        <p className="text-xs text-slate-400 mt-1">{limitsDescriptions.nps_contribution}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Standard Deduction</Label>
                        <Input 
                          type="number" 
                          value={subheadLimits.standard_deduction || ''} 
                          onChange={(e) => handleLimitChange('standard_deduction', e.target.value)}
                          placeholder={limitsDefaults.standard_deduction?.toString()}
                        />
                        <p className="text-xs text-slate-400 mt-1">{limitsDescriptions.standard_deduction}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <DialogFooter className="flex justify-between items-center">
                <Button 
                  variant="ghost" 
                  onClick={resetToDefaults}
                  disabled={limitsLoading || savingLimits}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset to Defaults
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowLimitsModal(false)} disabled={savingLimits}>Cancel</Button>
                  <Button 
                    onClick={handleSaveLimits} 
                    disabled={limitsLoading || savingLimits}
                    data-testid="save-limits-btn"
                  >
                    {savingLimits ? (
                      <>
                        <div className="spinner w-4 h-4 mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Limits
                      </>
                    )}
                  </Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default TaxDeclarations;
